import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { getStateNameByStateCode } from 'us-state-codes';
import getNationalAverage from '../../national-averages';

import { OPIOIDS_SHAPE } from '../../../../config/prop-types';

/**
 * Displays Opioid Prescribers data
 */
export default class OpioidTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onColumnClick = this.onColumnClick.bind(this);
  }
  /**
   * Handles sort clicks for table headers
   */
  onColumnClick(column) {
    const { sortedColumn, sortOrder } = this.props;
    // If the sorted column has changed, reset the sort order
    let newSortOrder;
    if (sortedColumn !== column) {
      newSortOrder = 'DESC';
    } else {
      newSortOrder = ('DESC' === sortOrder) ? 'ASC' : 'DESC';
    }
    this.props.handleTableSortClick(column, newSortOrder);
  }
  /**
   * Renders the sort control
   * @param {String} type - the column being sorted
   * @returns {object} - JSX for the control
   */
  sortControl(type) {
    const { sortedColumn, sortOrder } = this.props;
    if (type === sortedColumn && 'ASC' === sortOrder) {
      return (
        <div>
          <div className="upArrow active" />
          <div className="downArrow" />
        </div>
      );
    } else if (type === sortedColumn && 'DESC' === sortOrder) {
      return (
        <div>
          <div className="upArrow" />
          <div className="downArrow active" />
        </div>
      );
    }
    return (<div />);
  }
  /**
   * @returns {object} - JSX for the component.
   */
  render() {
    const {
      activeProcedure,
      data,
      loading,
    } = this.props;

    if (loading) {
      return (<div className="loading">Loading...</div>);
    } else if (! data.length) {
      return (<div className="noresults">
        There are no records that match the currently selected filters.
      </div>);
    }
    return (
      <div className="khnTable">
        <div className="key">
          <div className="item">
            <div className="color above-average" />
            <div className="title">Higher than average</div>
          </div>
          <div className="item">
            <div className="color below-average" />
            <div className="title">Lower than average</div>
          </div>
        </div>
        <table>
          <caption>Doctors prescribing opioids from 2011 to 2016</caption>
          <thead>
            <tr>
              <th scope="col">
                <button
                  className="sort-by"
                  onClick={() =>
                    this.onColumnClick('NAME')}
                >
                  Doctors performing {activeProcedure}
                  {this.sortControl('NAME')}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-by"
                  onClick={() =>
                    this.onColumnClick('RATE')}
                >
                  Pills per patient, 2011-2016
                  {this.sortControl('RATE')}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-by"
                  onClick={() =>
                    this.onColumnClick('PATIENTS')}
                >
                  Patients filling
                  {this.sortControl('PATIENTS')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((doctor) => (
              <tr key={doctor.id}>
                <th className="doctor-info" scope="row">
                  <div className="doctor-details">
                    <div className="doctor-details__name">
                      Dr. {doctor.nmf} {doctor.nmm}
                      &nbsp;{doctor.nml} {doctor.sx}
                    </div>
                    <div className="doctor-details__state">
                      {getStateNameByStateCode(doctor.st)}
                    </div>
                  </div>
                  <div className="doctor-hospitals">
                    {doctor.hp}
                  </div>
                </th>
                <td className={classNames({
                  'doctor-rate': true,
                  'below-avg':
                    Math.round(doctor.rt)
                    <= getNationalAverage(activeProcedure),
                  'above-avg':
                    Math.round(doctor.rt)
                    > getNationalAverage(activeProcedure),
                })}
                >
                  {doctor.rt.toFixed(0)}
                </td>
                <td className="doctor-patients">
                  {doctor.pt}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

OpioidTable.propTypes = {
  activeProcedure: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(OPIOIDS_SHAPE)).isRequired,
  handleTableSortClick: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  sortedColumn: PropTypes
  .oneOfType([PropTypes.number, PropTypes.string]),
  sortOrder: PropTypes.string.isRequired,
};

OpioidTable.defaultProps = {
  nationalAverage: null,
  sortedColumn: null,
};
